import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import SelectableCard from 'core/components/SelectableCard'
import Info from 'core/components/validatedForm/Info'
import ListTableField from 'core/components/validatedForm/ListTableField'
import Text from 'core/elements/Text'
import Radio from 'core/elements/input/Radio'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useListAction from 'core/hooks/useListAction'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  filterCreateVmNetworks,
  getCreateVmNetworkCounts,
} from '../CreateVirtualMachineModal.helpers'
import useCreateVirtualMachineModalStyles from '../CreateVirtualMachineModal.styles'
import { getManagedNetworkColumns, getSimpleNetworkColumns } from '../columns/networkColumns'
import {
  MANAGED_NETWORK_INFO,
  NetworkMode,
  SIMPLE_NETWORK_TOOLTIP_INFO,
} from '../../networks/constants'
import { listNetworks } from '../../networks/actions'
import { networksSelector } from '../../networks/selectors'

interface NetworkingControllerInput {
  currentTenant: string
  isSelfServiceUser: boolean
}

interface Props {
  getParamsUpdater: any
  loadingNetworks: boolean
  networksList: any[]
  params: any
  setMinAvailableIpsNetwork: (value: any) => void
  setVmNetworkMode: (value: NetworkMode | null) => void
  updateParams: any
  vmNetworkMode: NetworkMode | null
}

export function useCreateVirtualMachineNetworkingStepController({
  currentTenant,
  isSelfServiceUser,
}: NetworkingControllerInput) {
  const [vmNetworkMode, setVmNetworkMode] = useState<NetworkMode | null>(NetworkMode.MANAGED)

  const { loading: loadingNetworks, reload: reloadNetworks } = useListAction(listNetworks, {
    params: {},
    initialLoadingState: true,
  })
  const networks = useSelector(networksSelector)

  const networksList = useMemo(
    () =>
      filterCreateVmNetworks({
        networks,
        currentTenant,
        isSelfServiceUser,
      }),
    [currentTenant, isSelfServiceUser, networks],
  )

  const resetNetworkingStepState = () => {
    setVmNetworkMode(NetworkMode.MANAGED)
  }

  return {
    loadingNetworks,
    networksList,
    reloadNetworks,
    resetNetworkingStepState,
    setVmNetworkMode,
    vmNetworkMode,
  }
}

export default function CreateVirtualMachineNetworkingStep({
  getParamsUpdater,
  loadingNetworks,
  networksList,
  params,
  setMinAvailableIpsNetwork,
  setVmNetworkMode,
  updateParams,
  vmNetworkMode,
}: Props) {
  const classes = useCreateVirtualMachineModalStyles()
  const { managedNetworksCount, simpleNetworksCount } = useMemo(
    () => getCreateVmNetworkCounts(networksList),
    [networksList],
  )

  const simpleNetworkColumns = useMemo(() => getSimpleNetworkColumns(), [])

  const managedNetworkColumns = useMemo(
    () => getManagedNetworkColumns({ params, getParamsUpdater }),
    [getParamsUpdater, params],
  )

  const networkColumns = useMemo(() => {
    return vmNetworkMode === NetworkMode.SIMPLE ? simpleNetworkColumns : managedNetworkColumns
  }, [managedNetworkColumns, simpleNetworkColumns, vmNetworkMode])

  const filteredNetworksList = useMemo(() => {
    if (vmNetworkMode === NetworkMode.SIMPLE) {
      return networksList.filter((network) => network.isSimpleNetwork)
    }

    if (vmNetworkMode === NetworkMode.MANAGED) {
      return networksList.filter(
        (network) => !network.isSimpleNetwork && network.subnets?.length > 0,
      )
    }

    return networksList
  }, [networksList, vmNetworkMode])

  return (
    <div className={classes.fields}>
      <Info className={classes.info}>
        <div className={classes.infoContent}>
          <Text variant="caption1">Networking</Text>
          <Text variant="body2">
            Assign a network to the VM by selecting from available network pools.
          </Text>
        </div>
      </Info>

      <Text variant="body2" style={{ marginBottom: '16px', display: 'block', fontWeight: '600' }}>
        Select Network Type
      </Text>
      <div className={classes.networkModeCards}>
        <div className={classes.networkCardWrapper}>
          <SelectableCard
            id="managed"
            onClick={() => {
              setVmNetworkMode(NetworkMode.MANAGED)
              if (params.networks.length > 0) {
                getParamsUpdater('networks')([])
              }
              getParamsUpdater('selectedSubnets')({})
            }}
            active={vmNetworkMode === NetworkMode.MANAGED}
            className={classes.networkCard}
            withCustomBody
          >
            <div className={classes.networkCardBody}>
              <Radio checked={vmNetworkMode === NetworkMode.MANAGED} />
              <Text variant="caption1" className={classes.networkCardContent}>
                Managed Networks ({managedNetworksCount})
              </Text>
              <Tooltip customClassName={classes.networkTooltipBody} message={MANAGED_NETWORK_INFO}>
                <HelpOutlineIcon className={classes.networkInfoIcon} />
              </Tooltip>
            </div>
          </SelectableCard>
        </div>
        <div className={classes.networkCardWrapper}>
          <SelectableCard
            id="simple"
            onClick={() => {
              setVmNetworkMode(NetworkMode.SIMPLE)
              if (params.networks.length > 0) {
                getParamsUpdater('networks')([])
              }

              if (params.useSecurityGroups) {
                updateParams({ useSecurityGroups: false, securityGroups: [] })
              }

              getParamsUpdater('selectedSubnets')({})
            }}
            active={vmNetworkMode === NetworkMode.SIMPLE}
            className={classes.networkCard}
            withCustomBody
          >
            <div className={classes.networkCardBody}>
              <Radio checked={vmNetworkMode === NetworkMode.SIMPLE} />
              <Text variant="caption1" className={classes.networkCardContent}>
                Layer 2 Networks ({simpleNetworksCount})
              </Text>
              <Tooltip
                customClassName={classes.networkTooltipBody}
                message={SIMPLE_NETWORK_TOOLTIP_INFO}
              >
                <HelpOutlineIcon className={classes.networkInfoIcon} />
              </Tooltip>
            </div>
          </SelectableCard>
        </div>
      </div>

      <ListTableField
        id="networks"
        data={filteredNetworksList}
        loading={loadingNetworks}
        columns={networkColumns}
        onChange={(selectedNetworks) => {
          setMinAvailableIpsNetwork(null)
          getParamsUpdater('networks')(selectedNetworks)

          // Clean up selectedSubnets for deselected networks, initialize new networks to '' (Automatic)
          const updatedSubnets = Object.fromEntries(
            selectedNetworks.map((n) => [n.id, params.selectedSubnets?.[n.id] ?? '']),
          )
          getParamsUpdater('selectedSubnets')(updatedSubnets)
        }}
        value={params.networks}
        uniqueIdentifier="id"
        searchTargets={['name']}
        multiSelection
        noScroll
        required
        checkboxCond={(network) => {
          if (vmNetworkMode === null) {
            return true
          }

          if (vmNetworkMode === NetworkMode.SIMPLE) {
            return network.isSimpleNetwork
          }

          if (vmNetworkMode === NetworkMode.MANAGED) {
            if (network?.availablePortAndIPs <= 0) {
              return false
            }

            return !network.isSimpleNetwork && network.subnets?.length > 0
          }

          return true
        }}
        checkboxCondMessage={
          vmNetworkMode === NetworkMode.SIMPLE
            ? 'Only Simple Networks can be selected in Simple Network mode'
            : vmNetworkMode === NetworkMode.MANAGED
            ? 'Managed Network has no available IP addresses or ports'
            : 'Select a network mode first'
        }
      />
    </div>
  )
}
